import React, { useContext, useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FIELD_NAME_KEY,
  FILLABLE_COMBO_BOX_TYPE,
  OPTIONS_KEY,
  TEXT_DEBOUNCE_TIME_OUT,
} from '../../../utils/complexDocumentConstants';
import { PageElementsContext } from '../../../context/pageElementsContext';
import ComboBoxSettings from '../../../components/ComboBoxSettings';
import { ComboBoxGrp, ComboOption, FontSizeValue } from '../styles';
import {
  ELEMENTS_SETTINGS_TOOLBAR,
  TEXT_AREA_TOOLBAR,
} from '../../../artboard/Toolbar/translationValues';
import { COMBOBOX_SETTINGS_SIDEBAR } from '../../../components/ComboBoxSettings/translationValues';
import { Icon, Select, Tooltip, Popover } from 'antd';
import _ from 'underscore';
import {
  ColorButton,
  FontSpan,
  SizeButton,
  ButtonGroup,
  SizeContainer,
} from '../../../artboard/Toolbar/styles';
import ColorPicker from '../../../artboard/ColorPicker';
import { useTranslation } from 'react-i18next';
import FontSideBar from '../FontSideBar';
import { fontSizes } from '../../../utils/editor/fonts';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { deepCopy } from '../../../utils/javascript';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';

const { Option } = Select;
const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR', 'TEXT_AREA_TOOLBAR', 'COMBOBOX_SETTINGS_SIDEBAR'];
const getElementDetails = el => el;

const getOptionVariableString = (content = [], globalVariables) => {
  const result = [];
  for (let i = 0; i < content.length; i++) {
    const el = content[i];
    if (el.contentType === 'LINE_BREAK') {
      break;
    }
    if (el?.style?.isVariable) {
      const nVar = globalVariables?.find(
        globalVariable =>
          globalVariable.id === parseInt(el.content.replaceAll('{', '').replaceAll('}', '')),
      );
      result.push(nVar?.placeHolderText);
      continue;
    }
    result.push(el?.content);
  }
  return result.join('');
};

const ComboBoxEditor = props => {
  const { element, selected, editable, staticTableBlock, isComboboxGroup = false, isInteractiveDesign } = props;
  const [picker, setPicker] = useState(false);
  const [currentColor, seCurrentColor] = useState(null);
  const [visibleSizeOption, setVisibleSizeOption] = useState(false);
  const [isFontListVisible, setFontListVisible] = useState(false);
  const [defaultComboVal, setDefaultComboVal] = useState('');
  const [entering, setEntering] = useState(true);
  const { updateComplexElementDetails, isMovableClick } = useContext(PageElementsContext);
  const { t } = useTranslation(NAMESPACES);

  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables] = useDocumentContext(artboardSelector);

  const debouncedUpdateComplexElementDetails = useRef(
    _.debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT),
  ).current;

  const inputRef = useRef(null);
  let comboBoxValues = element?.contents;
  let elemStyles = element?.style;

  const selector = useCallback(store => store[CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE], []);
  const [elementSettingsVisible, updateArtboard] = useArtboardContext(selector);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const onFieldNameChange = useCallback(value => {
    debouncedUpdateComplexElementDetails(
      element.uuid,
      { options: { [FIELD_NAME_KEY]: value } },
      'form-options',
    );
    return value;
  }, []);

  const updateElementDetails = useCallback((value, updationType = 'contents') => {
    updateComplexElementDetails(element.uuid, value, updationType);
    return value;
  }, []);

  const showBackgroundColorPicker = useCallback(() => {
    seCurrentColor('backgroundColor');
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: element.style.backgroundColor };
    });
  }, [element]);

  const showBorderColorPicker = useCallback(() => {
    seCurrentColor('borderColor');
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: element.style.border?.borderColor };
    });
  }, [element]);

  const showTextColorPicker = useCallback(() => {
    seCurrentColor('textColor');
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'color') {
        return false;
      }
      return { type: 'textColor', color: element.style.color };
    });
  }, [element]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateComplexElementDetails(element.uuid, { backgroundColor: color.hex }, 'style');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        updateComplexElementDetails(element.uuid, { border: { borderColor: color.hex } }, 'style');
        setPicker({ type: 'borderColor', color: color.hex });
      } else if (picker.type === 'textColor') {
        updateComplexElementDetails(element.uuid, { color: color.hex }, 'style');
        setPicker({ type: 'textColor', color: color.hex });
      }
    },
    [picker],
  );

  const onFontClick = useCallback(() => {
    setFontListVisible(true);
  }, [element]);

  useEffect(() => {
    if (comboBoxValues?.length > 0) {
      const defaultEl = comboBoxValues.find(el => el.defaultValue);
      if (defaultEl) {
        setDefaultComboVal(getOptionVariableString(defaultEl?.content, globalVariables));
      } else {
        setDefaultComboVal(getOptionVariableString(comboBoxValues[0]?.content, globalVariables));
      }
    }
  }, [comboBoxValues]);

  const selectedVal = comboIndex => {
    const updatedValues = deepCopy(comboBoxValues).map((el, index) => {
      if (comboIndex === index) {
        el.defaultValue = true;
      } else {
        el.defaultValue = false;
      }
      return el;
    });
    updateElementDetails(updatedValues);
    const defaultEl = updatedValues.find(el => el.defaultValue);
    setDefaultComboVal(getOptionVariableString(defaultEl?.content, globalVariables));
  };

  const closeFontList = useCallback(() => {
    setFontListVisible(false);
  }, []);

  const renderFont = font => {
    return (
      <FontSpan>
        <Tooltip
          placement="top"
          title={t(
            `TEXT_AREA_TOOLBAR:${TEXT_AREA_TOOLBAR.TEXT_FONT_TOOLTIP.key}`,
            TEXT_AREA_TOOLBAR.TEXT_FONT_TOOLTIP.value,
          )}
        >
          <span className="ql-formats">
            <SizeButton
              width={165}
              value={font}
              comboBox={1}
              onClick={() => {
                onFontClick();
              }}
            >
              <span> {font}</span>
              <Icon type="down" />
            </SizeButton>
          </span>
        </Tooltip>
      </FontSpan>
    );
  };

  const renderSize = fontSize => {
    const displayFontSize = fontSize ? (fontSize / 1.334).toFixed(1) : '';
    return (
      <Tooltip
        placement="top"
        title={t(
          `TEXT_AREA_TOOLBAR:${TEXT_AREA_TOOLBAR.TEXT_FONT_SIZE_TOOLTIP.key}`,
          TEXT_AREA_TOOLBAR.TEXT_FONT_SIZE_TOOLTIP.value,
        )}
      >
        <span className="ql-formats" id="toolbar-fontsize">
          <>
            <ButtonGroup>
              <SizeButton
                comboBox={1}
                isMinus={1}
                onClick={() => {
                  updateComplexElementDetails(
                    element.uuid,
                    {
                      fontSize: (parseFloat(displayFontSize) - 1) * 1.334
                    },
                    'combobox-styles',
                  );
                }}
                icon="minus"
              />
              <>
                <Popover
                  content={
                    <SizeContainer>
                      <ul>
                        {fontSizes.map(data => (
                          <li
                            key={data}
                            onClick={() =>
                              updateComplexElementDetails(
                                element.uuid,
                                {
                                  fontSize: (parseFloat(data) * 1.334).toFixed(1)
                                },
                                'combobox-styles',
                              )
                            }
                          >
                            {data}
                          </li>
                        ))}
                      </ul>
                    </SizeContainer>
                  }
                  title={null}
                  trigger="click"
                  visible={visibleSizeOption}
                >
                  {/*TODO : Change this to contenteditable so that focus is not lost from text editor*/}
                  <FontSizeValue
                    suppressContentEditableWarning={true}
                    contentEditable={true}
                    className="size-ref"
                    data-ariaexpanded={visibleSizeOption}
                    ref={inputRef}
                    onKeyDown={e => handleKeypress(e)}
                    onClick={() => [setVisibleSizeOption(!visibleSizeOption)]}
                    onInput={e => {
                      setVisibleSizeOption(false);
                    }}
                    onBlur={e => {
                      if (!entering) return;
                      const value = parseFloat(inputRef.current.innerHTML.trim());
                      if (!isNaN(value) && value >= 1 && value <= 400) {
                        updateComplexElementDetails(
                          element.uuid,
                          {
                            fontSize: (value * 1.334).toFixed(1),
                            // height: parseInt((parseFloat(value) * 1.334).toFixed(1)) * 1.334,
                          },
                          'combobox-styles',
                        );
                      }
                      setVisibleSizeOption(false);
                    }}
                    onMouseEnter={e => setEntering(true)}
                    onMouseOut={e => setEntering(false)}
                  >
                    {fontSize ? (fontSize / 1.334).toFixed(1) : ''}
                  </FontSizeValue>
                </Popover>
              </>
              <SizeButton
                comboBox={1}
                onClick={() =>
                  updateComplexElementDetails(
                    element.uuid,
                    {
                      fontSize: (parseFloat(displayFontSize) + 1) * 1.334
                    },
                    'combobox-styles',
                  )
                }
                icon="plus"
              />
            </ButtonGroup>
          </>
        </span>
      </Tooltip>
    );
  };

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        updateComplexElementDetails(
          element.uuid,
          {
            fontSize: (value * 1.334).toFixed(1),
            // height: parseInt((parseFloat(value) * 1.334).toFixed(1)) * 1.334,
          },
          'combobox-styles',
        );
      }
    }
  };

  return (
    <>
      <ComboBoxGrp
        elemStyles={elemStyles}
        data-uuid={element.uuid}
        id={element.uuid}
        data-element-type={FILLABLE_COMBO_BOX_TYPE}
        onKeyUp={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        staticTableBlock={staticTableBlock}
        isComboboxGroup={isComboboxGroup}
      >
        <Select
          value={!isComboboxGroup ? defaultComboVal : 'value 1'}
          dropdownStyle={{
            background: elemStyles?.backgroundColor,
            fontSize: elemStyles?.fontSize,
          }}
          disabled={
            !isComboboxGroup ? isMovableClick || defaultComboVal?.length === 0 || !editable : isMovableClick || !editable
          }
          suffixIcon={<Icon type="caret-down" />}
          dropdownMenuStyle={{
            border: `1px solid ${elemStyles?.borderColor}`,
            marginTop: '-0.5px !important',
          }}
          style={{ fontFamily: elemStyles?.fontFamily, fontSize: elemStyles?.fontSize }}
          placeholder={t(
            `COMBOBOX_SETTINGS_SIDEBAR:${COMBOBOX_SETTINGS_SIDEBAR.COMBOBOX_PLACEHOLDER_LABEL.key}`,
            COMBOBOX_SETTINGS_SIDEBAR.COMBOBOX_PLACEHOLDER_LABEL.value,
          )}
          onChange={e => !isComboboxGroup && selectedVal(e)}
        >
          {!isComboboxGroup
            ? comboBoxValues?.map((comboValue, comboIndex) => (
                <Option className="comboOption" value={comboIndex}>
                  <ComboOption
                    className="ComboVal"
                    elemStyles={elemStyles}
                    staticTableBlock={staticTableBlock}
                  >
                    {getOptionVariableString(comboValue.content, globalVariables)}
                  </ComboOption>
                </Option>
              ))
            : ['value 1', 'value 2'].map(comboValue => (
                <Option className="comboOption" value={comboValue}>
                  <ComboOption
                    className="ComboVal"
                    elemStyles={elemStyles}
                    staticTableBlock={staticTableBlock}
                  >
                    {comboValue}
                  </ComboOption>
                </Option>
              ))}
        </Select>
      </ComboBoxGrp>
      {editable && selected && (
        <>
          {(!isFontListVisible && elementSettingsVisible) &&
            ReactDOM.createPortal(
              <ComboBoxSettings
                style={{ top: 10 }}
                element={element}
                onFieldNameChange={onFieldNameChange}
                updateElementDetails={updateElementDetails}
                FIELD_NAME_KEY={FIELD_NAME_KEY}
                OPTIONS_KEY={OPTIONS_KEY}
                getElementDetails={getElementDetails}
                isComboboxGroup={isComboboxGroup}
                isInteractiveDesign={isInteractiveDesign}
              />,
              document.getElementById('sidebar-absolute-container'),
            )}
          {ReactDOM.createPortal(
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              {renderFont(element.style.fontFamily)}
              {renderSize(element.style.fontSize)}
              <Tooltip
                placement="bottom"
                title={t(
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
                )}
              >
                <ColorButton
                  style={{
                    backgroundColor: element.style.backgroundColor,
                    position: 'relative',
                  }}
                  onClick={showBackgroundColorPicker}
                />
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
                )}
              >
                <ColorButton
                  style={{
                    backgroundColor: element.style.border?.borderColor,
                    position: 'relative',
                  }}
                  onClick={showBorderColorPicker}
                />
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.key,
                  ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.value,
                )}
              >
                <ColorButton
                  style={{
                    backgroundColor: element.style.color,
                    position: 'relative',
                  }}
                  onClick={showTextColorPicker}
                />
              </Tooltip>
              {picker && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right:
                      currentColor === 'textColor'
                        ? '0px'
                        : currentColor === 'borderColor'
                        ? '35px'
                        : '70px',
                  }}
                >
                  <ColorPicker
                    onChange={onColorPick}
                    onBlur={() => setPicker(false)}
                    color={picker.color}
                    qlEditor={true}
                  />
                </div>
              )}
            </div>,
            document.getElementById('document-editor-toolbar'),
          )}
        </>
      )}
      {selected && (
        <FontSideBar
          itemUUID={element.uuid}
          setFontChange={e => {
            updateComplexElementDetails(element.uuid, { fontFamily: e }, 'style');
          }}
          fontFamily={elemStyles.family}
          isToolBarVisible={isFontListVisible}
          closeFontList={closeFontList}
        />
      )}
    </>
  );
};

export default ComboBoxEditor;
